import React, { KeyboardEvent, PureComponent, RefObject } from 'react';
import Loader from '../Loader/Loader';
import InfiniteScroll from '../utils/InfiniteScroll';
import scrollIntoView from '../utils/scrollIntoView';
import {
  DATA_HOOKS,
  DATA_OPTION,
  DATA_SHOWN,
  DATA_DIRECTION,
  DROPDOWN_LAYOUT_DIRECTIONS,
  OPTION_DATA_HOOKS,
  DROPDOWN_LAYOUT_LOADER,
  DATA_SELECTED_OPTION_ID,
} from './DataAttr';
import { st, classes } from './DropdownLayout.st.css';
import { filterObject } from '../utils/filterObject';
import { listItemSectionBuilder } from '../ListItemSection';
import { listItemSelectBuilder } from '../ListItemSelect';
import { listItemActionBuilder } from '../ListItemAction';
import { isString } from '../utils/StringUtils';
import {
  withOverlayScrollbar,
  WithOverlayScrollbarProps,
} from './withOverlayScrollbar';
import {
  DIVIDER_OPTION_VALUE,
  KEY,
  ListType,
  MOUSE_EVENTS_SUPPORTED,
  NOT_HOVERED_INDEX,
  Scrollbar,
} from './DropdownLayout.constants';
import { forkRef } from '../utils/forkRef';
import {
  DropdownLayoutBuilderOption,
  DropdownLayoutDividerOption,
  DropdownLayoutOption,
  DropdownLayoutProps,
  DropdownLayoutValueOption,
} from './DropdownLayout.types';
import { ListItemActionRefProps } from '../ListItemAction/ListItemAction.types';
import { createHoverHandlers } from '../common/useHover';

const modulus = (n: number, m: number): number => ((n % m) + m) % m;

const getUnit = (value: string | number): string =>
  isString(value) ? value : `${value}px`;

type FocusedItemId = string | number | null;
type SelectedOption = HTMLDivElement | null;
type OnClickEvent = { id: FocusedItemId };

type State = {
  hovered: number;
  selectedId: DropdownLayoutProps['selectedId'];
  scrollElement: HTMLElement | null;
  focusedItemId: FocusedItemId;
};

type DropdownLayoutChildren = {
  [key: string | number]: {
    focus?: () => void;
    wrappedComponentRef?: {
      innerComponentRef: {
        focus: () => void;
      };
    };
  } | null;
};

type ConvertedCustomOption = Pick<
  DropdownLayoutValueOption,
  | 'id'
  | 'disabled'
  | 'disabledDescription'
  | 'overrideStyle'
  | 'overrideOptionStyle'
> &
  Pick<DropdownLayoutBuilderOption, 'value'>;

type ConvertedOption =
  | ConvertedCustomOption
  | DropdownLayoutBuilderOption
  | undefined;

export class DropdownLayout extends PureComponent<
  DropdownLayoutProps & WithOverlayScrollbarProps,
  State
> {
  static defaultProps: DropdownLayoutProps = {
    options: [],
    tabIndex: 0,
    maxHeightPixels: 260,
    closeOnSelect: true,
    itemHeight: 'small',
    selectedHighlight: true,
    inContainer: false,
    infiniteScroll: false,
    loadMore: undefined,
    hasMore: false,
    markedOption: false,
    overflow: 'auto',
    listType: ListType.select,
    scrollbar: 'fixed',
  };

  static displayName = 'DropdownLayout';

  static NONE_SELECTED_ID: -1 = NOT_HOVERED_INDEX;

  constructor(props: DropdownLayoutProps) {
    super(props);

    this.state = {
      hovered: NOT_HOVERED_INDEX,
      selectedId: props.selectedId,
      scrollElement: null,
      focusedItemId: null,
    };
  }

  setScrollElement = (node: HTMLElement | null) => {
    this.setState({ scrollElement: node });
  };

  containerRef = React.createRef<HTMLDivElement>();
  optionsRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  loadedWithUndefinedOptions = false;
  _boundEvents: string[] = [];
  focusableItemsIdsList: (string | number)[] = [];
  savedOnClicks: {
    id?: string | number;
    onClick: (e: OnClickEvent) => void;
  }[] = [];
  children: DropdownLayoutChildren = {};
  selectedOption: SelectedOption = null;

  componentDidMount() {
    if (this.loadedWithUndefinedOptions && this.optionsRef.current) {
      this.forceUpdate();
    }

    const { focusOnSelectedOption, scrollToOption, autoFocus } = this.props;

    if (focusOnSelectedOption) {
      this._focusOnSelectedOption();
    } else if (this.props.hasOwnProperty('focusOnOption')) {
      this._focusOnOption();
    }
    if (scrollToOption) {
      this._scrollToOption();
    }

    this._markOptionByProperty(this.props);

    /** @deprecated */
    MOUSE_EVENTS_SUPPORTED.forEach(eventName => {
      document.addEventListener(eventName, this._onMouseEventsHandler, true);
    });

    this._boundEvents = MOUSE_EVENTS_SUPPORTED;

    if (!this._isDialogMode() && autoFocus) {
      this._focusFirstOption();
    }
  }

  componentWillUnmount() {
    if (this._boundEvents && typeof document !== 'undefined') {
      this._boundEvents.forEach(eventName => {
        document.removeEventListener(
          eventName,
          this._onMouseEventsHandler,
          true,
        );
      });
    }
  }

  componentDidUpdate(prevProps: DropdownLayoutProps) {
    const { focusOnOption } = this.props;
    if (prevProps.focusOnOption !== focusOnOption) {
      this._focusOnOption();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: DropdownLayoutProps) {
    const options = this.props.options || [];
    const nextOptions = nextProps.options || [];

    if (this.props.visible !== nextProps.visible) {
      this._markOption(NOT_HOVERED_INDEX);
    }

    if (this.props.selectedId !== nextProps.selectedId) {
      this.setState({ selectedId: nextProps.selectedId });
    }

    // make sure the same item is hovered if options changed
    if (
      this.state.hovered !== NOT_HOVERED_INDEX &&
      (!nextOptions[this.state.hovered] ||
        options[this.state.hovered].id !== nextOptions[this.state.hovered].id)
    ) {
      this._markOption(
        this._findIndex(
          nextProps.options ?? [],
          item => item.id === options[this.state.hovered].id,
        ),
      );
    }

    this._markOptionByProperty(nextProps);
  }

  _getOptionElementId(optionIndex: number) {
    return `dropdown-item-${optionIndex}`;
  }

  _isDialogMode = () => {
    /** This component has two modes:
     *  1. Menu mode: when fixedHeader or fixedFooter is not present - https://www.w3.org/WAI/ARIA/apg/patterns/menubar/
     *  2. Dialog mode: when fixedHeader or fixedFooter is present - https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/
     */
    const { fixedHeader, fixedFooter } = this.props;
    return Boolean(fixedHeader || fixedFooter);
  };

  _focusFirstOption() {
    this._focus(this.focusableItemsIdsList[0]);
  }

  /** @deprecated */
  _checkIfEventOnElements(e: any, elem: any) {
    let current = e.target;
    while (current.parentNode) {
      if (elem.indexOf(current) > -1) {
        return true;
      }
      current = current.parentNode;
    }

    return current !== document;
  }

  /** @deprecated */
  _onMouseEventsHandler = (e: any) => {
    if (!this._checkIfEventOnElements(e, [this.containerRef.current])) {
      this._onClickOutside(e);
    }
  };

  /** @deprecated */
  _renderTopArrow() {
    const { withArrow, visible } = this.props;

    return withArrow && visible ? (
      <div data-hook={DATA_HOOKS.TOP_ARROW} className={classes.arrow} />
    ) : null;
  }

  _convertOptionToListItemSectionBuilder({
    option,
    idx,
  }: {
    option: DropdownLayoutDividerOption | DropdownLayoutValueOption;
    idx: number;
  }): ReturnType<typeof listItemSectionBuilder> | undefined {
    if (this._isDivider(option)) {
      return listItemSectionBuilder({
        dataHook: OPTION_DATA_HOOKS.DIVIDER,
        id: option.id || idx,
        type: 'divider',
      });
    } else if (option.title) {
      return listItemSectionBuilder({
        dataHook: OPTION_DATA_HOOKS.TITLE,
        id: option.id,
        type: 'subheader',
        title: option.value as React.ReactNode,
      });
    } else {
      // This should never happen but let's make TS happy
      return undefined;
    }
  }

  _convertOptionToListItemActionBuilder({
    option,
    idx,
  }: {
    option: DropdownLayoutValueOption;
    idx: number;
  }): ReturnType<typeof listItemActionBuilder> {
    const {
      id,
      value,
      disabled,
      disabledDescription,
      optionTitle,
      title,
      ...rest
    } = option;
    const { size } = this.props;
    return listItemActionBuilder({
      id: id !== undefined ? id : idx,
      ref: (ref: ListItemActionRefProps | null) => (this.children[id] = ref),
      tabIndex: id === this.state.focusedItemId && !disabled ? '0' : '-1',
      disabled,
      disabledDescription,
      title: optionTitle!,
      role: 'menuitem',
      size,
      ...rest,
    });
  }

  _isControlled() {
    return (
      typeof this.props.selectedId !== 'undefined' &&
      typeof this.props.onSelect !== 'undefined'
    );
  }

  _focusOnSelectedOption() {
    if (this.selectedOption && this.optionsRef.current) {
      this.optionsRef.current.scrollTop = Math.max(
        this.selectedOption.offsetTop - this.selectedOption.offsetHeight,
        0,
      );
    }
  }

  _setSelectedOptionNode(
    optionNode: HTMLDivElement | null,
    option: DropdownLayoutBuilderOption | ConvertedCustomOption,
  ) {
    if (option.id === this.state.selectedId) {
      this.selectedOption = optionNode;
    }
  }

  _onClickOutside = (event: TouchEvent | MouseEvent) => {
    const { visible, onClickOutside } = this.props;
    if (visible && onClickOutside) {
      onClickOutside(event);
    }
  };

  _markOption(index: number, options?: DropdownLayoutOption[]) {
    const { onOptionMarked } = this.props;
    options = options || this.props.options || [];

    this.setState({ hovered: index });

    if (onOptionMarked) {
      const markedOption = options[index] || null;
      const markedOptionElementId =
        index === NOT_HOVERED_INDEX
          ? undefined
          : this._getOptionElementId(index);

      // the type declaration was incorrect, casting to avoid breaking changes after TS conversion
      onOptionMarked(
        markedOption as DropdownLayoutValueOption | null,
        markedOptionElementId,
      );
    }
  }

  _onSelect = (
    index: number,
    e: React.KeyboardEvent<Element> | React.MouseEvent<Element, MouseEvent>,
  ) => {
    const { onSelect, listType } = this.props;
    const options = this.props.options || [];

    if (listType !== ListType.select) {
      e.stopPropagation();
      this._onClose();
      return;
    }

    const chosenOption = options[index];

    if (chosenOption) {
      const sameOptionWasPicked = chosenOption.id === this.state.selectedId;
      if (onSelect) {
        e.stopPropagation();
        onSelect(
          // casting to avoid breaking changes after TS conversion
          chosenOption as DropdownLayoutValueOption,
          sameOptionWasPicked,
        );
      }
    }
    if (!this._isControlled()) {
      this.setState({ selectedId: chosenOption && chosenOption.id });
    }
    return !!onSelect && chosenOption;
  };

  _onActionClick = (e: OnClickEvent) => {
    const onClick = this.savedOnClicks.find(({ id }) => id === e.id)?.onClick;
    onClick && onClick(e);
  };

  _saveOnClicks = () => {
    const options: DropdownLayoutOption[] = this.props.options || [];

    this.savedOnClicks = options.map(
      ({
        id,
        // only builder options have onClick
        // @ts-expect-error
        onClick,
      }) => ({
        id,
        onClick,
      }),
    );
  };

  _onOptionHoverStart = (index: number) => {
    const { options = [] } = this.props;
    if (this._isSelectableOption(options[index])) {
      const markedOption = options[index] as DropdownLayoutValueOption;
      this.props.onOptionsNavigate?.(markedOption);
      this._markOption(index);
    }
  };

  _onOptionHoverEnd = () => {
    this._markOption(NOT_HOVERED_INDEX);
  };

  _onContainerMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    this.props.onOptionsNavigate?.(null);
    this._markOption(NOT_HOVERED_INDEX);
    if (this.props.onMouseLeave) {
      this.props.onMouseLeave(e);
    }
  };

  _getMarkedIndex() {
    const options = this.props.options || [];
    const useHoverIndex = this.state.hovered > NOT_HOVERED_INDEX;
    const useSelectedIdIndex = typeof this.state.selectedId !== 'undefined';

    let markedIndex;
    if (useHoverIndex) {
      markedIndex = this.state.hovered;
    } else if (useSelectedIdIndex) {
      markedIndex = options.findIndex(
        option => option.id === this.state.selectedId,
      );
    } else {
      markedIndex = NOT_HOVERED_INDEX;
    }

    return markedIndex;
  }

  getActiveDescendentElementId() {
    const options = this.props.options || [];

    let markedIndex = this._getMarkedIndex();

    if (markedIndex === NOT_HOVERED_INDEX) {
      markedIndex = options.findIndex(this._isSelectableOption);
    }

    return markedIndex === NOT_HOVERED_INDEX
      ? undefined
      : this._getOptionElementId(markedIndex);
  }

  _markNextStep(step: number) {
    const { onOptionsNavigate, options = [] } = this.props;

    if (!options.some(this._isSelectableOption)) {
      return;
    }

    let markedIndex = this._getMarkedIndex();

    do {
      markedIndex = modulus(Math.max(markedIndex + step, -1), options.length);
    } while (!this._isSelectableOption(options[markedIndex]));

    const markedOption = options[markedIndex] as DropdownLayoutValueOption;
    onOptionsNavigate?.(markedOption);
    this._markOptionAtIndex(markedIndex);
  }

  _focusOnOption = () => {
    const { focusOnOption } = this.props;
    const options = this.props.options || [];

    const markedIndex = options.findIndex(
      option => option.id === focusOnOption,
    );

    if (markedIndex !== -1) {
      this._markOptionAtIndex(markedIndex);
    } else {
      // Remove focus
      this._markOption(markedIndex);
    }
  };

  _scrollToOption() {
    const { scrollToOption } = this.props;
    const options = this.props.options || [];

    const optionIndex = options.findIndex(
      option => option.id === scrollToOption,
    );

    const optionNode = this.optionsRef.current?.childNodes[optionIndex] as
      | HTMLElement
      | undefined;

    if (!optionNode || !this.optionsRef.current) {
      return;
    }

    this.optionsRef.current.scrollTop = Math.max(
      optionNode.offsetTop - optionNode.offsetHeight,
      0,
    );
  }

  _markOptionAtIndex = (markedIndex: number) => {
    this._markOption(markedIndex);
    this._scrollIntoViewByIndex(markedIndex);
  };

  _scrollIntoViewByIndex = (index: number) => {
    const { infiniteScroll } = this.props;

    const menuElement = this.optionsRef.current;
    const hoveredElement = infiniteScroll
      ? this.optionsRef.current?.childNodes[0].childNodes[index]
      : this.optionsRef.current?.childNodes[index];

    scrollIntoView(menuElement, hoveredElement);
  };

  /**
   * Handle keydown events for the DropdownLayout, mostly for accessibility
   *
   * @param event - The keydown event triggered by React
   * @returns Whether the event was handled by the component
   */
  _onSelectListKeyDown = (
    event: KeyboardEvent<Element>,
  ): boolean | DropdownLayoutOption | undefined => {
    if (
      !this.props.visible ||
      // such prop does not exist but let's avoid potential breaking change
      // @ts-expect-error
      this.props.isComposing ||
      this.props.listType !== ListType.select
    ) {
      return false;
    }

    switch (event.key) {
      case KEY.arrowDown: {
        if (this._isArrowNavigationDisabled(event)) {
          return false;
        }

        this._markNextStep(1);
        event.preventDefault();
        event.stopPropagation();
        return true;
      }

      case KEY.arrowUp: {
        if (this._isArrowNavigationDisabled(event)) {
          return false;
        }

        this._markNextStep(-1);
        event.preventDefault();
        event.stopPropagation();
        return true;
      }

      case KEY.space:
      case KEY.enter: {
        // If event is from fixedHeader/fixedFooter, let it bubble naturally
        const isFromFixedRegion = this._isEventFromFixedRegion(event);

        if (isFromFixedRegion) {
          // Don't preventDefault, don't stopPropagation - let button handle it completely
          return false;
        }

        // Otherwise, handle as option selection (when options container is focused)
        if (this.state.hovered !== NOT_HOVERED_INDEX) {
          this._onSelect(this.state.hovered, event);
          if (this.props.closeOnSelect) {
            this._onClose();
          }
          return true;
        }
        // If no option is hovered, return false to allow manual input handling
        return false;
      }

      case KEY.tab: {
        // Dialog mode: let natural Tab navigation work within dialog
        if (this._isDialogMode()) {
          return false;
        }

        // Menu mode: handle Tab key behavior
        // If an option is hovered, select it first (similar to Enter/Space)
        if (this.state.hovered !== NOT_HOVERED_INDEX) {
          event.preventDefault();
          this._onSelect(this.state.hovered, event);
          if (this.props.closeOnSelect) {
            this._onClose();
          }
          return true;
        }

        // No hovered option: close dropdown and determine if parent should handle Tab
        this._onClose();

        // Check if Tab originated from an input field with text
        // If so, return false to allow parent component (e.g., InputWithOptions)
        // to handle Tab and trigger onManuallyInput callback
        // Otherwise, return true to indicate we handled it (prevents DropdownBase double-closing)
        const inputValue = (event.target as HTMLInputElement)?.value?.trim();
        const hasInputText = Boolean(inputValue);

        // Return false to allow parent handling, true to indicate we handled it
        return !hasInputText;
      }

      case KEY.arrowRight: {
        // Delegate to the component-level onDrillIn callback so the consumer
        // can move focus into an interactive child element (e.g. an actions menu).
        if (this.state.hovered === NOT_HOVERED_INDEX) {
          return false;
        }

        const { onDrillIn } = this.props;
        if (typeof onDrillIn === 'function') {
          const options = this.props.options ?? [];
          const hoveredOption = options[
            this.state.hovered
          ] as DropdownLayoutValueOption;
          onDrillIn(hoveredOption);
          event.preventDefault();
          event.stopPropagation();
          return true;
        }

        return false;
      }

      case KEY.escape: {
        this._onClose();
        event.stopPropagation();
        return true;
      }

      default: {
        return false;
      }
    }
  };

  _focus = (focusedItemId: FocusedItemId, e?: React.SyntheticEvent) => {
    e && e.preventDefault();
    const element = this.children[focusedItemId!];

    if (!element) {
      return;
    }

    const native = element.focus;
    const focusableHOC = element.wrappedComponentRef;

    const callback = native
      ? element.focus
      : focusableHOC
        ? focusableHOC.innerComponentRef.focus
        : () => ({});

    this.setState({ focusedItemId }, () => callback?.());
  };

  _handleActionListNavigation = (
    event: React.KeyboardEvent,
    id: string | number,
  ) => {
    const length = this.focusableItemsIdsList.length;
    let focusedItemId = this.state.focusedItemId;
    const { key } = event;
    const currentMenuItemIndex = this.focusableItemsIdsList.indexOf(id);
    const firstMenuItem = this.focusableItemsIdsList[0];
    const lastMenuItem = this.focusableItemsIdsList[length - 1];

    if (key === KEY.arrowLeft || key === KEY.arrowUp) {
      focusedItemId =
        id === 0
          ? lastMenuItem
          : this.focusableItemsIdsList[currentMenuItemIndex - 1];
    }

    if (key === KEY.arrowRight || key === KEY.arrowDown) {
      focusedItemId =
        currentMenuItemIndex === length - 1
          ? firstMenuItem
          : this.focusableItemsIdsList[currentMenuItemIndex + 1];
    }

    if (key === KEY.home) {
      focusedItemId = firstMenuItem;
    }

    if (key === KEY.end) {
      focusedItemId = lastMenuItem;
    }

    if (focusedItemId !== this.state.focusedItemId) {
      this._focus(focusedItemId, event);
      this._scrollIntoViewByIndex(focusedItemId as number);
    }
  };

  _onActionListKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    id: string | number,
  ) => {
    // Action lists use direct focus on each item, so they have their own
    // keyboard handling that must run first.
    if (this.props.listType === ListType.action) {
      const { key } = event;

      if (key === KEY.space || key === KEY.enter) {
        event.preventDefault();
        this._onActionClick({ id: this.state.focusedItemId });
        this._onClose();
      } else if (key === KEY.escape || key === KEY.tab) {
        this._onClose();
      } else {
        this._handleActionListNavigation(event, id);
      }

      event.stopPropagation();
      return;
    }

    // For non-action lists: when focus is on an interactive child element
    // (e.g. a button the user drilled into via ArrowRight), handle keyboard
    // navigation back to the options container.
    const optionEl = event.currentTarget;
    const activeEl = document.activeElement;
    const containsFocusedElement =
      optionEl.contains(activeEl) && activeEl !== optionEl;

    if (!containsFocusedElement) {
      return;
    }

    const { key } = event;

    const restoreFocusToList = () => {
      if (this.props.onDrillOut) {
        this.props.onDrillOut();
      } else if (this.optionsRef.current) {
        this.optionsRef.current.focus();
      }
    };

    if (key === KEY.escape || key === KEY.arrowLeft) {
      event.preventDefault();
      event.stopPropagation();
      restoreFocusToList();
    }

    if (key === KEY.enter || key === KEY.space) {
      event.stopPropagation();
    }

    if (key === KEY.arrowDown || key === KEY.arrowUp) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  _onClose = () => {
    this.props.onOptionsNavigate?.(null);
    this._markOption(NOT_HOVERED_INDEX);

    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  _isEventFromFixedRegion(event: React.KeyboardEvent | KeyboardEvent): boolean {
    const target = event.target as HTMLElement;
    const region = target.closest('[data-dropdown-region]');

    if (!region) {
      return false;
    }

    const regionValue = region.getAttribute('data-dropdown-region');
    return regionValue === 'header' || regionValue === 'footer';
  }

  _isArrowNavigationDisabled(
    event: React.KeyboardEvent | KeyboardEvent,
  ): boolean {
    // Disable arrow navigation only when event originates from fixed header/footer region
    return this._isEventFromFixedRegion(event);
  }

  _renderNode(node: React.ReactNode, region?: 'header' | 'footer') {
    if (!node) return null;

    return (
      <div data-dropdown-region={region} onFocus={this._onFixedRegionFocus}>
        {node}
      </div>
    );
  }

  _onFixedRegionFocus = () => {
    // Clear the highlighted/active list item when focus moves to header/footer button
    if (this.state.hovered !== NOT_HOVERED_INDEX) {
      this._markOption(NOT_HOVERED_INDEX);
    }
  };

  _wrapWithInfiniteScroll = (scrollableElement: React.ReactNode) => {
    if (!this.optionsRef.current) {
      this.loadedWithUndefinedOptions = true;
    }

    return (
      <InfiniteScroll
        useWindow
        dataHook={DATA_HOOKS.INFINITE_SCROLL_CONTAINER}
        scrollElement={this.state.scrollElement}
        loadMore={this.props.loadMore}
        hasMore={this.props.hasMore}
        data={this.props.options}
        loader={
          <div className={classes.loader}>
            <Loader dataHook={DROPDOWN_LAYOUT_LOADER} size="small" />
          </div>
        }
      >
        {scrollableElement}
      </InfiniteScroll>
    );
  };

  /** for testing purposes only */
  _getDataAttributes = () => {
    const { visible, dropDirectionUp } = this.props;
    const { selectedId } = this.state;

    return filterObject(
      {
        'data-hook': DATA_HOOKS.CONTENT_CONTAINER,
        [DATA_SHOWN]: visible,
        [DATA_SELECTED_OPTION_ID]:
          selectedId === 0 ? `${selectedId}` : selectedId,
        [DATA_DIRECTION]: dropDirectionUp
          ? DROPDOWN_LAYOUT_DIRECTIONS.UP
          : DROPDOWN_LAYOUT_DIRECTIONS.DOWN,
      },
      (_, value) => !!value,
    );
  };

  _convertCustomOptionToBuilder({
    option,
  }: {
    option: DropdownLayoutValueOption;
  }): ConvertedCustomOption | undefined {
    const {
      value,
      id,
      disabled,
      disabledDescription,
      overrideOptionStyle,
      overrideStyle,
    } = option;

    if (overrideStyle) {
      return {
        id,
        disabled,
        disabledDescription,
        overrideStyle,
        value: () => (
          <div className={classes.dropdownOption} data-hook={DATA_HOOKS.OPTION}>
            {value as React.ReactNode}
          </div>
        ),
      };
    }

    if (overrideOptionStyle) {
      return {
        id,
        disabled,
        disabledDescription,
        overrideOptionStyle,
        value: () => (
          <div className={classes.dropdownOption} data-hook={DATA_HOOKS.OPTION}>
            {value as React.ReactNode}
          </div>
        ),
      };
    } else {
      // This should never happen but let's make TS happy
      return undefined;
    }
  }

  _convertOptionToListItemSelectBuilder({
    option,
  }: {
    option: DropdownLayoutValueOption;
  }): ReturnType<typeof listItemSelectBuilder> {
    const { value, id, disabled, disabledDescription } = option;
    const { selectedId } = this.state;
    const { itemHeight, selectedHighlight, size } = this.props;
    return listItemSelectBuilder({
      id,
      title: (
        <div data-hook={DATA_HOOKS.OPTION}>{value as React.ReactNode}</div>
      ),
      disabled,
      disabledDescription,
      size,
      selected: id === selectedId && selectedHighlight,
      className: st(classes.selectableOption, { itemHeight }),
    });
  }

  _isBuilderOption(
    option: DropdownLayoutOption,
  ): option is DropdownLayoutBuilderOption {
    const { value } = option;
    return typeof value === 'function';
  }

  _isCustomOption(option: DropdownLayoutValueOption) {
    const { overrideOptionStyle, overrideStyle } = option;
    return overrideOptionStyle || overrideStyle;
  }

  _isActionOption(
    option: DropdownLayoutOption,
  ): option is DropdownLayoutValueOption {
    return 'value' in option && option.value === ListType.action;
  }

  _isItemSection(
    option: DropdownLayoutOption,
  ): option is DropdownLayoutDividerOption | DropdownLayoutValueOption {
    // only DropdownLayoutValueOption type has title prop, which is deprecated
    // therefore expecting error instead of dealing with it
    // @ts-expect-error
    const { title: isTitle } = option;

    return this._isDivider(option) || isTitle;
  }

  _isDivider(opt: DropdownLayoutOption): opt is DropdownLayoutDividerOption {
    return opt.value === DIVIDER_OPTION_VALUE;
  }

  _convertOptionToBuilder(
    option: DropdownLayoutOption,
    idx: number,
  ): ConvertedOption {
    if (this._isBuilderOption(option)) {
      return option;
    } else if (this._isActionOption(option)) {
      return this._convertOptionToListItemActionBuilder({ option, idx });
    } else if (this._isItemSection(option)) {
      return this._convertOptionToListItemSectionBuilder({ option, idx });
    } else if (this._isCustomOption(option)) {
      return this._convertCustomOptionToBuilder({ option });
    } else {
      return this._convertOptionToListItemSelectBuilder({ option });
    }
  }

  _renderOption({
    option,
    idx,
  }: {
    option: DropdownLayoutOption;
    idx: number;
  }): React.ReactElement | undefined {
    const isValueOption = (
      opt: DropdownLayoutOption,
    ): opt is DropdownLayoutValueOption =>
      'id' in opt && 'value' in opt && typeof opt.value !== 'function';
    const builderOption = this._convertOptionToBuilder(option, idx);
    const hasLink = isValueOption(option) && !!option.linkTo;
    const content = this._renderOptionContent({
      option: builderOption,
      idx,
      hasLink,
    });

    const isActionItem = this.props.listType === ListType.action;

    return hasLink ? (
      <a
        className={classes.linkItem}
        key={idx}
        data-hook={DATA_HOOKS.LINK_ITEM}
        href={option.linkTo}
        role={isActionItem ? undefined : 'option'}
        aria-selected={
          isActionItem || option.disabled
            ? undefined
            : option.id === this.state.selectedId
        }
        aria-hidden={option.disabled}
      >
        {content}
      </a>
    ) : (
      content
    );
  }

  // For testing purposes only
  _getItemDataAttr = ({
    hovered,
    selected,
    disabled,
  }: {
    hovered: boolean;
    selected: boolean;
    disabled?: boolean;
  }) => {
    const { itemHeight, selectedHighlight } = this.props;

    return filterObject(
      {
        [DATA_OPTION.DISABLED]: disabled,
        [DATA_OPTION.SELECTED]: selected && selectedHighlight,
        [DATA_OPTION.HOVERED]: hovered,
        /* deprecated */
        [DATA_OPTION.SIZE]: itemHeight,
      },
      (_, value) => !!value,
    );
  };

  _renderOptionContent({
    option,
    idx,
    hasLink,
  }: {
    option: ConvertedOption;
    idx: number;
    hasLink: boolean;
  }): React.ReactElement | undefined {
    if (!option) {
      return undefined;
    }
    const { itemHeight, selectedHighlight, listType } = this.props;
    const { selectedId, hovered } = this.state;

    const {
      id,
      disabled,
      // Only ListItemAction and ListItemSelect have disabledDescription prop
      // @ts-expect-error
      disabledDescription,
      // only ConvertedCustomOption type has overrideStyle prop, which is deprecated
      // therefore expecting error instead of dealing with it
      // @ts-expect-error
      overrideStyle,
      overrideOptionStyle,
    } = option;

    const optionState = {
      selected: id === selectedId,
      hovered: idx === hovered,
      disabled,
      size: this.props.size,
    };
    const isDisabledWithTooltip = disabled && !!disabledDescription;

    if (!disabled || isDisabledWithTooltip) {
      this.focusableItemsIdsList = [...this.focusableItemsIdsList, id];
    }

    const isActionItem = listType === ListType.action;

    return (
      <div
        aria-selected={
          hasLink || isActionItem || disabled ? undefined : optionState.selected
        }
        aria-hidden={disabled}
        {...this._getItemDataAttr({ ...optionState })}
        role={hasLink || isActionItem ? undefined : 'option'}
        className={
          overrideOptionStyle
            ? null
            : st(classes.option, {
                ...optionState,
                selected: optionState.selected && selectedHighlight,
                itemHeight,
                overrideStyle,
              })
        }
        id={this._getOptionElementId(idx)}
        ref={node => this._setSelectedOptionNode(node, option)}
        onClick={!disabled ? e => this._onSelect(idx, e) : undefined}
        key={idx}
        {...createHoverHandlers({
          onHoverStart: () => this._onOptionHoverStart(idx),
          onHoverEnd: () => this._onOptionHoverEnd(),
        })}
        data-hook={`dropdown-item-${id}`}
        onKeyDown={e => this._onActionListKeyDown(e, id)}
      >
        {option?.value(optionState)}
      </div>
    );
  }

  _markOptionByProperty(props: DropdownLayoutProps) {
    const options = props.options || [];

    if (this.state.hovered === NOT_HOVERED_INDEX && props.markedOption) {
      const selectableOptions = options.filter(this._isSelectableOption);
      if (selectableOptions.length) {
        const idToMark =
          props.markedOption === true
            ? selectableOptions[0].id
            : props.markedOption;
        const foundIndex = this._findIndex(
          options,
          (item: DropdownLayoutOption) => item.id === idToMark,
        );

        if (foundIndex !== -1) {
          this._markOption(foundIndex, props.options);
        }
      }
    }
  }

  _findIndex(
    arr: DropdownLayoutOption[],
    predicate: (item: DropdownLayoutOption) => boolean,
  ): number {
    return (Array.isArray(arr) ? arr : []).findIndex(predicate);
  }

  _isSelectableOption = (option: DropdownLayoutOption): boolean => {
    if (!option) {
      return false;
    }

    if (this._isDivider(option)) {
      return false;
    }

    // only DropdownLayoutValueOption type has title prop, which is deprecated
    // therefore expecting error instead of dealing with it
    // @ts-expect-error
    const hasTitle = option.title;

    return !this._isDivider(option) && !option.disabled && !hasTitle;
  };

  _renderOptions(): (React.ReactElement | undefined)[] {
    this.focusableItemsIdsList = [];
    this._saveOnClicks();
    const options = this.props.options || [];

    return options.map((option, idx) => this._renderOption({ option, idx }));
  }

  render() {
    const {
      className,
      visible,
      dropDirectionUp,
      onMouseEnter,
      onMouseDown,
      fixedHeader,
      withArrow,
      fixedFooter,
      inContainer,
      overflow,
      maxHeightPixels = 260,
      minWidthPixels,
      infiniteScroll,
      dataHook,
      listType,
      overlayScrollbarProps,
      listboxId,
    } = this.props;

    const renderedOptions = this._renderOptions();

    const OverlayScrollbarHostElement =
      overlayScrollbarProps?.OverlayScrollbarHostElement;
    const OverlayScrollbarContentElement =
      overlayScrollbarProps?.OverlayScrollbarContentElement;

    const optionsContainerProps = {
      style: {
        maxHeight: getUnit(parseInt(String(maxHeightPixels), 10) - 35),
        overflow,
      },
      'data-hook': DATA_HOOKS.DROPDOWN_LAYOUT_OPTIONS,
      role: listType === ListType.select ? 'listbox' : 'menu',
      id: listboxId,
      // Make options container focusable as a focus stop in Modal Dialog pattern
      tabIndex: this._isDialogMode() ? 0 : -1,
      // Use aria-activedescendant pattern (W3C standard)
      'aria-activedescendant':
        this.state.hovered !== NOT_HOVERED_INDEX
          ? this._getOptionElementId(this.state.hovered)
          : undefined,
    };

    const options = infiniteScroll
      ? this._wrapWithInfiniteScroll(renderedOptions)
      : renderedOptions;

    const optionsContainer =
      this.props.scrollbar === Scrollbar.OVERLAY &&
      OverlayScrollbarHostElement &&
      OverlayScrollbarContentElement ? (
        <OverlayScrollbarHostElement
          className={classes.options}
          {...optionsContainerProps}
        >
          <OverlayScrollbarContentElement
            className={classes.optionsContent}
            ref={forkRef<HTMLDivElement>(
              this.optionsRef,
              this.setScrollElement,
            )}
          >
            {options}
          </OverlayScrollbarContentElement>
        </OverlayScrollbarHostElement>
      ) : (
        <div
          className={st(classes.options, classes.optionsContent)}
          ref={forkRef<HTMLDivElement>(this.optionsRef, this.setScrollElement)}
          {...optionsContainerProps}
        >
          {options}
        </div>
      );

    const isEmpty =
      !fixedHeader &&
      !fixedFooter &&
      !this.props.options?.length &&
      (!this.props.infiniteScroll || !this.props.hasMore);

    return (
      <div
        data-list-type={listType}
        data-hook={dataHook}
        className={st(
          classes.root,
          {
            hidden: isEmpty,
            visible,
            withArrow,
            direction: dropDirectionUp
              ? DROPDOWN_LAYOUT_DIRECTIONS.UP
              : DROPDOWN_LAYOUT_DIRECTIONS.DOWN,
            containerStyles: !inContainer,
          },
          className,
        )}
        onKeyDown={this._onSelectListKeyDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={this._onContainerMouseLeave}
        onMouseDown={onMouseDown}
        ref={this.containerRef}
      >
        <div
          {...this._getDataAttributes()}
          className={classes.contentContainer}
          style={{
            overflow,
            maxHeight: maxHeightPixels ? getUnit(maxHeightPixels) : undefined,
            minWidth: minWidthPixels ? getUnit(minWidthPixels) : undefined,
          }}
        >
          {this._renderNode(fixedHeader, 'header')}
          {optionsContainer}
          {this._renderNode(fixedFooter, 'footer')}
        </div>
        {this._renderTopArrow()}
      </div>
    );
  }
}

export default withOverlayScrollbar(DropdownLayout);
